import React, { useState, type FC } from "react";
import { MenuIcon, TableLayoutIcon } from "@omega/icons";
import { useAppBackHandler } from "@app/back";
import { TaskNavigation } from "@app/menu/TaskNavigation";
import { TaskTilesNavigation } from "@app/menu/TaskTilesNavigation";
import { findNodeById, findParentId } from "@app/menu/taskTreeHelpers";
import { useTaskViewMode } from "@app/menu/useTaskViewMode";
import { useTaskTree } from "@app/tasks/useTaskTree";
import { useLatestCallback } from "@shared/hooks/useLatestCallback";
import { ButtonIcon } from "@shared/ui/button";
import "../Menu/Menu.scss";

/**
 * Экран списка задач OMP (дерево модулей и листьев). \
 * Поддерживает списочный и плиточный (drill-down) режимы с persistence выбора.
 */
const Tasks: FC = () => {
    const { nodes } = useTaskTree({ hideTasksWithoutRoute: true });
    const { viewMode, toggleViewMode } = useTaskViewMode();
    const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);

    const currentFolder = currentFolderId != null ? findNodeById(nodes, currentFolderId) : null;
    const title = currentFolder?.name ?? "Задачи";
    const isTiles = viewMode === "tiles";

    const handleToggleViewMode = (): void => {
        setCurrentFolderId(null);
        toggleViewMode();
    };

    const handleAppBack = useLatestCallback((): boolean => {
        if (!isTiles || currentFolderId == null) {
            return false;
        }

        setCurrentFolderId(findParentId(nodes, currentFolderId));
        return true;
    });

    useAppBackHandler(handleAppBack);

    return (
        <div className="app_menu">
            <header className="app_menu__header">
                <h1 className="app_menu__title">{title}</h1>
                <ButtonIcon
                    className="app_menu__view_toggle"
                    size="sm"
                    variant="ghost"
                    aria-label={isTiles ? "Переключить на список" : "Переключить на плитки"}
                    aria-pressed={isTiles}
                    title={isTiles ? "Список" : "Плитки"}
                    onClick={handleToggleViewMode}
                >
                    {isTiles ? <MenuIcon size={18} /> : <TableLayoutIcon size={18} />}
                </ButtonIcon>
            </header>
            <main className="app_menu__body">
                {isTiles ? (
                    <TaskTilesNavigation
                        nodes={nodes}
                        currentFolderId={currentFolderId}
                        onFolderChange={setCurrentFolderId}
                    />
                ) : (
                    <TaskNavigation nodes={nodes} />
                )}
            </main>
        </div>
    );
};

export default Tasks;
